/**
 * Loading placeholders.
 *
 * A bar the size of the thing that is coming, rather than a word telling you to wait.
 * Two reasons beyond looks: the page does not jump when the data lands, and a
 * table that is slow to answer still shows how much is on its way.
 *
 * Every skeleton is aria-hidden and the container carries aria-busy, so a
 * screen reader is told the region is loading instead of being read a dozen
 * decorative boxes.
 */
export function Shim({ w = '100%', h = 12, style }: {
  w?: number | string; h?: number; style?: React.CSSProperties;
}) {
  return <span className="shim" aria-hidden="true"
    style={{ width: typeof w === 'number' ? `${w}px` : w, height: h, ...style }} />;
}

/** Widths that vary a little, so a loading table does not look like graph paper. */
const WIDTHS = ['70%', '45%', '58%', '38%', '64%', '50%', '42%'];

export function SkeletonRows({ cols, rows = 5, tick = false }: {
  cols: number; rows?: number; tick?: boolean;
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-busy="true">
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              {tick && c === 0
                ? <Shim w={14} h={14} style={{ borderRadius: 4 }} />
                : <Shim w={WIDTHS[(r + c) % WIDTHS.length]} h={c === cols - 1 ? 22 : 12} />}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** For a screen that is a form or a set of cards rather than a table. */
export function SkeletonPage({ bars = 6 }: { bars?: number }) {
  return (
    <div className="pg" aria-busy="true">
      <Shim w={220} h={26} style={{ marginBottom: 10 }} />
      <Shim w={320} h={13} style={{ marginBottom: 22 }} />
      <div className="crd" style={{ padding: '20px 22px', display: 'grid', gap: 13 }}>
        {Array.from({ length: bars }, (_, i) => (
          <Shim key={i} w={WIDTHS[i % WIDTHS.length]} h={14} />
        ))}
      </div>
    </div>
  );
}
